// Statement_Complete_Fix.jsx
import React, { useState, useEffect, useContext } from "react";
import { API_URL } from "../../components/api";
import { formatDateTime } from "../../../utils/dateFormat";
import Modal from "react-bootstrap/Modal";

import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarExport,
  GridFooterContainer,
  GridPagination,
} from "@mui/x-data-grid";

import ExcelSVG from "./ExcelSVG.svg";
import FilterSVG from "./FilterSVG.svg";
import Button from "react-bootstrap/Button";
import * as XLSX from "xlsx";
import AuthContext from "../../context/AuthContext";
import axios from "axios";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

import "./statement.css";
import logo from "./logo.png";
import logo2 from "./Omega_Speed_logo.jpeg";

function Statement(props) {
  const { user } = useContext(AuthContext);
  const authTokens = JSON.parse(localStorage.getItem("authTokens") || "{}");

  // Data + UI state (mirrors ONUTables)
  const [data, setData] = useState([]); // raw entries from API
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [gridFilterModel, setGridFilterModel] = useState({ items: [] });
  const [searchInput, setSearchInput] = useState("");
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [searchFilterTimeout, setSearchFilterTimeout] = useState(null);
  const { onClose } = props;
  const [detailRow, setDetailRow] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const isArDetail = document.documentElement.lang === "ar";
  const detailT = (en, ar) => (isArDetail ? ar : en);

  const openDetail = async (row) => {
    const target = { type: row.type, id: row.invoice_receipt_id };
    setDetailRow(target);
    setDetailData(null);
    setDetailError("");
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API_URL}/inventory/statement/detail/`, {
        headers: { Authorization: `Bearer ${authTokens?.access}` },
        params: target,
      });
      setDetailData(response.data);
    } catch (err) {
      setDetailError(detailT("Could not load the details.", "تعذّر تحميل التفاصيل."));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailRow(null);
    setDetailData(null);
    setDetailError("");
  };

  const detailLine = (label, value) => (
    <div style={{ display: "flex", gap: 12, padding: "3px 0" }}>
      <div style={{ minWidth: 130, opacity: 0.75 }}>{label}</div>
      <div style={{ flex: 1, wordBreak: "break-word" }}>
        {value === null || value === undefined || value === "" ? "-" : value}
      </div>
    </div>
  );

  const detailSection = (title) => (
    <div
      style={{
        marginTop: 14,
        marginBottom: 6,
        paddingBottom: 4,
        fontWeight: 600,
        borderBottom: "1px solid rgba(255,255,255,0.15)",
      }}
    >
      {title}
    </div>
  );

  const detailText = (text, key) => (
    <div key={key} style={{ padding: "3px 0" }}>
      {text}
    </div>
  );

  const detailDate = (value) => (value ? formatDateTime(value) : "-");

  const renderCancel = (cancel) => {
    if (!cancel) return null;
    return (
      <>
        {detailSection(detailT("Cancellation", "الإلغاء"))}
        {cancel.by ? (
          <>
            {detailLine(detailT("Cancelled by", "ألغاها"), cancel.by)}
            {detailLine(detailT("Cancelled on", "تاريخ الإلغاء"), detailDate(cancel.at))}
            {detailLine(detailT("Reason", "السبب"), cancel.reason)}
            {cancel.matched === "amount" &&
              detailText(
                detailT(
                  "Older cancel, matched by client and amount.",
                  "إلغاء قديم، تمت المطابقة حسب الزبون والمبلغ."
                )
              )}
          </>
        ) : (
          detailText(detailT("Reason not recorded.", "السبب غير مسجّل."))
        )}
      </>
    );
  };

  // Modal/per-column filters object (keys = column.field)
  const [filters, setFilters] = useState({
    invoice_receipt_id: "",
    date: "",
    description: "",
    type: "",
    total_amount: "",
    discount: "",
    status: "",
    paid_amount_usd: "",
    paid_amount_lbp: "",
    discount_usd: "",
    discount_lbp: "",
    returned_usd: "",
    returned_lbp: "",
    exchange_rate: "",
    balance_record: "",
  });

  // client info (full name preserved as in old code)
  const [clientName, setClientName] = useState("");
  const [villageName, setVillageName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  //for checkbox
  const [selectedRows, setSelectedRows] = useState([]);

  // Columns (kept same as original)
  const columns = [
    {
      field: "checkbox",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      renderHeader: () => (
        <input
          type="checkbox"
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows(filteredRows.map((row) => row.id));
            } else {
              setSelectedRows([]);
            }
          }}
          checked={
            selectedRows.length === filteredRows.length &&
            filteredRows.length > 0
          }
        />
      ),
      renderCell: (params) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(params.row.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, params.row.id]);
            } else {
              setSelectedRows(
                selectedRows.filter((id) => id !== params.row.id)
              );
            }
          }}
        />
      ),
    },
    {
      field: "invoice_receipt_id",
      headerName: "Invoice/Receipt ID",
      width: 150,
      filterable: true,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            lineHeight: "1.2",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "date",
      headerName: "Date",
      width: 120,
      filterable: true,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            lineHeight: "1.2",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      width: 300, // Changed from 200
      filterable: true,
      renderCell: (params) => (
        <div
          style={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            lineHeight: "1.4", // Changed from 1.2
            padding: "12px 8px", // Added this line
            maxHeight: "none", // Added this line
            overflow: "visible", // Added this line
          }}
        >
          {params.value}
        </div>
      ),
    },
    { field: "type", headerName: "Type", width: 100, filterable: true },
    {
      field: "total_amount",
      headerName: "Total Amount",
      width: 120,
      filterable: true,
    },
    { field: "discount", headerName: "Discount", width: 100, filterable: true },
    { field: "status", headerName: "Status", width: 100, filterable: true },
    {
      field: "paid_amount_usd",
      headerName: "Paid Amount USD",
      width: 140,
      filterable: true,
    },
    {
      field: "paid_amount_lbp",
      headerName: "Paid Amount LBP",
      width: 140,
      filterable: true,
    },
    {
      field: "discount_usd",
      headerName: "Discount USD",
      width: 120,
      filterable: true,
    },
    {
      field: "discount_lbp",
      headerName: "Discount LBP",
      width: 120,
      filterable: true,
    },
    {
      field: "returned_usd",
      headerName: "Returned USD",
      width: 120,
      filterable: true,
    },
    {
      field: "returned_lbp",
      headerName: "Returned LBP",
      width: 120,
      filterable: true,
    },
    {
      field: "exchange_rate",
      headerName: "Exchange Rate",
      width: 120,
      filterable: true,
    },
    {
      field: "balance_record",
      headerName: "Balance Record",
      width: 130,
      filterable: true,
    },
  ];

  // Fetch data from statement endpoint
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/inventory/statement/`, {
        headers: {
          Authorization: `Bearer ${authTokens?.access}`,
          "Content-Type": "application/json",
        },
        params: {
          username: user.username,
          client_id: props.client_id,
        },
      });
      if (response.status === 200) {
        const entries = (response.data.entries || []).map((entry, idx) => ({
          ...entry,
          id: idx,
          invoice_receipt_id:
            entry.type === "invoice" ? entry.invoice_id : entry.receipt_id,
        }));
        setData(entries);
        // preserve full name exactly as old code does
        setClientName(response.data.fullname_en || "");
        setVillageName(response.data.village_name || "");
        setPhoneNumber(response.data.phone_number || "");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.username, authTokens?.access, props.client_id]);

  // session storage for search (like ONUTables)
  useEffect(() => {
    const saved = sessionStorage.getItem("searchStatement");
    Object.keys(sessionStorage).forEach((key) => {
      if (key !== "searchStatement") sessionStorage.removeItem(key);
    });
    if (saved) {
      sessionStorage.setItem("searchStatement", saved);
      setSearchQuery(saved);
      setSearchInput(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const delaySearchFilter = (input) => {
    setSearchQuery(input);
  };

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchInput(q);
    sessionStorage.setItem("searchStatement", q);

    if (searchFilterTimeout) clearTimeout(searchFilterTimeout);
    const t = setTimeout(() => delaySearchFilter(q), 100);
    setSearchFilterTimeout(t);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // Combined filtering logic (searchQuery, modal filters, grid filter model)
  const filteredRows = data.filter((row) => {
    // searchQuery: split into words, each must match somewhere
    const words = (searchQuery || "")
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);
    const searchMatch = words.length
      ? words.every((w) =>
          Object.values(row).some((v) =>
            String(v ?? "")
              .toLowerCase()
              .includes(w)
          )
        )
      : true;

    if (!searchMatch) return false;

    // modal/per-column filters - substring contains
    const modalMatch = Object.keys(filters).every((k) => {
      if (!filters[k]) return true;
      const rv = String(row[k] ?? "").toLowerCase();
      return rv.includes(String(filters[k]).toLowerCase());
    });
    if (!modalMatch) return false;

    // gridFilterModel (DataGrid filters)
    const gridMatch = (gridFilterModel.items || []).every((fi) => {
      if (!fi.value) return true;
      const cellValue = row[fi.field];
      const a = String(cellValue ?? "").toLowerCase();
      const b = String(fi.value).toLowerCase();
      switch (fi.operator) {
        case "contains":
          return a.includes(b);
        case "equals":
          return a === b;
        case "startsWith":
          return a.startsWith(b);
        case "endsWith":
          return a.endsWith(b);
        default:
          return true;
      }
    });
    if (!gridMatch) return false;

    return true;
  });

  // Initialize columnVisibilityModel to show all by default
  useEffect(() => {
    const initial = {};
    columns.forEach((c) => {
      if (c.field === "checkbox") {
        initial[c.field] = true; // Always visible, not toggleable
      } else {
        initial[c.field] = true;
      }
    });
    setColumnVisibilityModel(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Export transform — respects columnVisibilityModel
  const transformRowsForExport = (rows) => {
    return rows.map((r) => {
      const t = {};
      columns.forEach((col) => {
        if (columnVisibilityModel[col.field] !== false) {
          t[col.headerName] = r[col.field] ?? "";
        }
      });
      return t;
    });
  };

  const handleExportToExcel = () => {
    try {
      const transformed = transformRowsForExport(filteredRows);
      const ws = XLSX.utils.json_to_sheet(transformed);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Statement");
      XLSX.writeFile(wb, `statement_${props.client_id || "export"}.xlsx`);
    } catch (e) {
      console.error("Export failed", e);
      alert("Export failed");
    }
  };

  // ----- FIXED print helpers -----
  async function imgToDataUrlViaCanvas(src) {
    return new Promise((resolve) => {
      try {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            const dataUrl = canvas.toDataURL("image/png");
            resolve(dataUrl);
          } catch (e) {
            console.warn("canvas convert failed", e);
            resolve(null);
          }
        };
        img.onerror = () => resolve(null);
        img.src = src;
      } catch (err) {
        resolve(null);
      }
    });
  }

  // Print: uses filteredRows and visible columns (so it respects search / filters / visibility)
  const handlePrint = async () => {
    try {
      // Use selected rows if any are selected, otherwise use all filtered rows
      const rowsToPrint =
        selectedRows.length > 0
          ? filteredRows.filter((row) => selectedRows.includes(row.id))
          : filteredRows;

      // visible columns in the same order as columns array (exclude checkbox column)
      const visibleColumns = columns.filter(
        (col) =>
          columnVisibilityModel[col.field] !== false && col.field !== "checkbox"
      );

      // Build table headers for visible columns only
      const headerHtml = visibleColumns
        .map((col) => `<th>${col.headerName}</th>`)
        .join("");

      // Build table rows with visible columns only
      const rowsHtml = (rowsToPrint || [])
        .map((entry) => {
          const cells = visibleColumns
            .map((col) => {
              let value = entry[col.field];
              if (value === undefined || value === null) value = "N/A";
              return `<td>${String(value).replace(/</g, "&lt;")}</td>`;
            })
            .join("");
          return `<tr>${cells}</tr>`;
        })
        .join("");

      let logoDataUrl = await imgToDataUrlViaCanvas(logo2);

      if (!logoDataUrl && typeof logo2 === "string") {
        try {
          const abs =
            logo2.startsWith("http") || logo2.startsWith("//")
              ? logo2
              : window.location.origin +
                (logo2.startsWith("/") ? "" : "/") +
                logo2;
          logoDataUrl = (await imgToDataUrlViaCanvas(abs)) || abs;
        } catch (e) {
          logoDataUrl = logo2;
        }
      }

      if (!logoDataUrl) logoDataUrl = "";

      const currentDate = formatDateTime(new Date());

      const printContents = `
      <html>
        <head>
          <title>Statement of Account</title>
          <meta name="viewport" content="width=device-width,initial-scale=1" />
          <link rel="stylesheet" href="https://stackpath.bootstrapcdn.com/bootstrap/4.3.1/css/bootstrap.min.css">
          <style>
            @media print { 
              @page { size: A4; margin: 1cm; } 
              /* Remove height restrictions for print */
              .table-container {
                max-height: none !important;
                overflow: visible !important;
                border: none !important;
              }
              /* Allow table rows to break across pages */
              table { page-break-inside: auto; }
              tr { page-break-inside: avoid; page-break-after: auto; }
              thead { display: table-header-group; } /* Repeat header on each page */
              tfoot { display: table-footer-group; }
            }
            
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; padding: 20px; }
            .container { max-width: 1200px; margin: 0 auto; }
            .header { display:flex; justify-content:space-between; align-items:center; margin-bottom:30px; padding-bottom:20px; border-bottom:2px solid #eee; }
            .logo { max-width:200px; height:auto; }
            .date { font-size:14px; color:#666; }
            .title { text-align:center; margin:30px 0; color:#2c3e50; }
            .company-info { text-align:center; margin-bottom:30px; color:#666; font-size:14px; }
            .client-info { background-color:#f8f9fa; padding:20px; border-radius:5px; margin-bottom:30px; }
            
            /* Table container - scrollable on screen, full height for print */
            .table-container {
              overflow: auto;
              max-height: 60vh;
              border: 1px solid #eee;
              padding: 6px;
              border-radius: 4px;
            }
            
            table { width:100%; border-collapse:collapse; margin-bottom:30px; min-width: 800px; }
            th { background-color:#2c3e50; color:white; padding:12px 8px; text-align:left; font-size:14px; }
            td { padding:12px 8px; border-bottom:1px solid #ddd; font-size:14px; }
            tr:nth-child(even) td { background:#f8f9fa; }
            .footer { margin-top:30px; padding-top:20px; border-top:2px solid #eee; }
            .total-balance { text-align:right; font-size:18px; color:#2c3e50; font-weight:bold; }
            .print-button { display:none; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <img id="__os_logo" src="${logoDataUrl}" class="logo" alt="Logo"/>
              <div class="date">${currentDate}</div>
            </div>
            <div class="title"><h2>STATEMENT OF ACCOUNT</h2></div>
            <div class="company-info">
              <p>Omega Smart Systems<br/>Bakaata Main Road, El Chouf, Lebanon<br/>Phone: 00961-76 842 442</p>
            </div>
            <div class="client-info">
              <strong>BILL TO:</strong><br/>
              <strong>Client's Name:</strong> ${clientName}<br/>
              <strong>Client's Location:</strong> ${villageName}<br/>
              <strong>Client's Phone Number:</strong> ${phoneNumber}
            </div>

            <div class="table-container">
              <table>
                <thead>
                  <tr>${headerHtml}</tr>
                </thead>
                <tbody>
                  ${rowsHtml}
                </tbody>
              </table>
            </div>

            <div class="footer">
              <div class="total-balance">Current Balance: ${currentBalanceUSD()} USD</div>
            </div>
          </div>

          <script>
            (function(){
              var img = document.getElementById('__os_logo');
              function doPrint(){ try{ window.focus(); window.print(); } catch(e){ console.error(e); } }
              if (!img) { setTimeout(doPrint, 120); return; }
              if (img.complete) { setTimeout(doPrint, 120); return; }
              var fired = false;
              img.addEventListener('load', function(){ if (!fired) { fired = true; setTimeout(doPrint, 80); }});
              img.addEventListener('error', function(){ if (!fired) { fired = true; setTimeout(doPrint, 80); }});
              setTimeout(function(){ if (!fired) { fired = true; doPrint(); } }, 3000);
            })();
          </script>
        </body>
      </html>
    `;

      const w = window.open("", "_blank", "width=1000,height=800");
      if (!w) {
        alert("Please allow popups to print the statement.");
        return;
      }
      w.document.open();
      w.document.write(printContents);
      w.document.close();
    } catch (err) {
      console.error("Print failed", err);
      alert("Unable to prepare print. See console for details.");
    }
  };
  // ----- end fixed print helpers -----

  // total based on filteredRows
  const calculateTotalUSD = () => {
    return (
      filteredRows.reduce((total, entry) => {
        const amount =
          entry.type === "invoice"
            ? parseFloat(entry.total_amount || 0)
            : -parseFloat(entry.paid_amount_usd || 0);
        return total + (isNaN(amount) ? 0 : amount);
      }, 0) || 0
    ).toFixed(2);
  };

  const currentBalanceUSD = () => {
    const value = Number(props.balance_usd || 0);
    return (isNaN(value) ? 0 : value).toFixed(2);
  };

  // Toolbar export pressed state
  const [isPressed, setIsPressed] = useState(false);
  const handleExportClick = () => {
    setIsPressed(true);
    handleExportToExcel();
    setTimeout(() => setIsPressed(false), 200);
  };

  // Custom toolbar & footer (mirror ONUTables), now includes Print button
  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <Button
        onClick={() => setShowFilterModal(true)}
        variant="outline-primary"
        style={{ border: "none" }}
      >
        <img
          src={FilterSVG}
          alt="Filter Icon"
          style={{ width: "18px", marginRight: "8px" }}
        />
        Filter
      </Button>

      <GridToolbarExport />
      <Button
        onClick={handleExportClick}
        variant="outline-success"
        style={{
          display: "flex",
          alignItems: "center",
          padding: "5px 10px",
          fontSize: "14px",
          border: "none",
          backgroundColor: isPressed ? "white" : "transparent",
          color: isPressed ? "black" : "#007bff",
          cursor: "pointer",
        }}
      >
        <img
          src={ExcelSVG}
          alt="Excel Icon"
          style={{
            width: "20px",
            marginRight: "8px",
            filter: isPressed ? "invert(0)" : "invert(1)",
          }}
        />
        Excel
      </Button>

      {/* PRINT BUTTON */}
      <Button
        onClick={handlePrint}
        variant="outline-secondary"
        style={{ marginLeft: 8 }}
      >
        Print
      </Button>
      {selectedRows.length > 0 && (
        <div
          style={{
            marginLeft: "8px",
            padding: "5px 10px",
            backgroundColor: "#e3f2fd",
            borderRadius: "4px",
            fontSize: "14px",
            color: "#1976d2",
          }}
        >
          {selectedRows.length} row(s) selected
        </div>
      )}
    </GridToolbarContainer>
  );

  const CustomFooter = () => (
    <GridFooterContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          padding: "10px",
        }}
      >
        <GridPagination />
      </div>
    </GridFooterContainer>
  );

  // DataGrid row id
  const getRowId = (row) => row.invoice_receipt_id ?? row.id;

  return (
    <div className="ClientTable" style={{ padding: "16px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          width: "100%",
        }}
      >
        <h4 className="head" style={{ margin: 0 }}>
          Records for {clientName}
        </h4>

        <Button size="sm" onClick={() => onClose && onClose()}>
          ✕ Close
        </Button>
      </div>
      <Dialog
        open={detailRow !== null}
        onClose={closeDetail}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { background: "#122632", color: "#fff" } }}
      >
        <DialogTitle
          dir={isArDetail ? "rtl" : "ltr"}
          style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}
        >
          {detailRow &&
            `${
              detailRow.type === "receipt"
                ? detailT("Receipt", "إيصال")
                : detailT("Invoice", "فاتورة")
            } #${detailRow.id}`}
        </DialogTitle>
        <DialogContent dir={isArDetail ? "rtl" : "ltr"} style={{ paddingTop: 12 }}>
          {detailLoading && <div>{detailT("Loading...", "جارٍ التحميل...")}</div>}
          {!detailLoading && detailError && (
            <div style={{ color: "#ff8a8a" }}>{detailError}</div>
          )}
          {!detailLoading && detailData && detailData.type === "invoice" && (
            <>
              {detailLine(detailT("Status", "الحالة"), detailData.status)}
              {detailLine(detailT("Date", "التاريخ"), detailDate(detailData.date))}
              {detailLine(detailT("Created by", "أنشأها"), detailData.initiator)}
              {detailLine(detailT("Description", "الوصف"), detailData.description)}
              {detailLine(detailT("Total", "المجموع"), `${detailData.total_amount} USD`)}
              {detailLine(detailT("Discount", "الحسم"), `${detailData.discount} USD`)}
              {detailData.order &&
                detailLine(detailT("Order", "الطلب"), `#${detailData.order.order_id}`)}
              {detailData.note && detailLine(detailT("Note", "ملاحظة"), detailData.note)}
              {detailSection(detailT("What was ordered", "ما تم طلبه"))}
              {detailData.items.length === 0
                ? detailText(detailT("No items recorded.", "لا أصناف مسجّلة."))
                : detailData.items.map((it, i) =>
                    detailText(
                      `${it.quantity} × ${it.name}${
                        it.is_free ? ` (${detailT("free", "مجاني")})` : ""
                      }`,
                      i
                    )
                  )}
              {detailSection(detailT("Payments", "الدفعات"))}
              {detailData.payments.length === 0
                ? detailText(detailT("No payments.", "لا دفعات."))
                : detailData.payments.map((p, i) =>
                    detailText(
                      `${detailT("Receipt", "إيصال")} #${p.receipt_id} · ${p.allocated_usd} USD · ${detailDate(p.date)} · ${p.status} · ${p.initiator || "-"}`,
                      i
                    )
                  )}
              {renderCancel(detailData.cancel)}
            </>
          )}
          {!detailLoading && detailData && detailData.type === "receipt" && (
            <>
              {detailLine(detailT("Status", "الحالة"), detailData.status)}
              {detailLine(detailT("Date", "التاريخ"), detailDate(detailData.date))}
              {detailLine(detailT("Collected by", "قبضها"), detailData.initiator)}
              {detailLine(detailT("Drawer", "الدرج"), detailData.drawer)}
              {detailLine(detailT("Description", "الوصف"), detailData.description)}
              {detailLine(detailT("Paid USD", "المدفوع بالدولار"), detailData.paid_amount_usd)}
              {detailLine(detailT("Paid LBP", "المدفوع بالليرة"), detailData.paid_amount_lbp)}
              {detailLine(detailT("Discount USD", "الحسم بالدولار"), detailData.discount_usd)}
              {detailLine(detailT("Discount LBP", "الحسم بالليرة"), detailData.discount_lbp)}
              {detailLine(detailT("Returned USD", "المرتجع بالدولار"), detailData.returned_usd)}
              {detailLine(detailT("Returned LBP", "المرتجع بالليرة"), detailData.returned_lbp)}
              {detailLine(detailT("Exchange rate", "سعر الصرف"), detailData.exchange_rate)}
              {detailSection(detailT("Invoices paid", "الفواتير المدفوعة"))}
              {detailData.invoices.length === 0
                ? detailText(detailT("None.", "لا شيء."))
                : detailData.invoices.map((inv, i) =>
                    detailText(
                      `${detailT("Invoice", "فاتورة")} #${inv.invoice_id} · ${inv.allocated_usd} USD · ${inv.description || ""}`,
                      i
                    )
                  )}
              {detailData.edits.length > 0 && (
                <>
                  {detailSection(detailT("Edits", "التعديلات"))}
                  {detailData.edits.map((e, i) =>
                    detailText(`${e.by} · ${detailDate(e.at)}`, i)
                  )}
                </>
              )}
              {renderCancel(detailData.cancel)}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="sm" onClick={closeDetail}>
            {detailT("Close", "إغلاق")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Search input */}
      <style>{`.MuiDataGrid-panelWrapper { background: #122632 !important; }`}</style>
      <input
        type="text"
        placeholder="Search..."
        value={searchInput}
        onChange={handleSearchChange}
        className="search-client"
      />
      {!loading && data.length === 0 && (
  <div
    style={{
      marginTop: 16,
      padding: 18,
      border: "1px dashed #b8b8b8",
      borderRadius: 8,
      backgroundColor: "#fafafa",
      color: "#333",
    }}
  >
    <strong>No invoice or receipt history found for this client.</strong>
    <div style={{ marginTop: 8 }}>
      This client still has an existing balance of ${currentBalanceUSD()} USD from the balance record.
    </div>
  </div>
)}
      {!loading && data.length > 0 && (
        <div style={{ height: 520, width: "100%" }}>
          <DataGrid
            getRowHeight={() => "auto"}
            rows={filteredRows}
            columns={columns}
            getRowId={getRowId}
            filterModel={gridFilterModel}
            onFilterModelChange={(newModel) => setGridFilterModel(newModel)}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            components={{
              Footer: () => <CustomFooter />,
              Toolbar: CustomToolbar,
            }}
            rowsPerPageOptions={[10, 100, 1000]}
            pagination
            disableSelectionOnClick
            onCellClick={(params) => {
              if (params.field === "checkbox") return;
              openDetail(params.row);
            }}
            sx={{
              fontFamily: "'Arial', 'Helvetica', sans-serif",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "rgb(18, 38, 50)",
                color: "#ffffff",
                fontWeight: "600",
              },
              "& .MuiDataGrid-cell": {
                outline: "none !important",
                padding: "12px 8px", // Changed from "8px"
                display: "flex",
                alignItems: "center",
                overflow: "visible", // Add this line
              },
              "& .MuiDataGrid-row": {
                // Add this whole section
                maxHeight: "none !important",
              },
              "& .MuiDataGrid-virtualScroller": { overflowX: "auto" },
              "& .MuiDataGrid-row:hover": { cursor: "pointer" },
              "& .MuiDataGrid-columnHeaderTitle": { textAlign: "center" },
            }}
          />
        </div>
      )}
      {/* Filter modal (mirrors ONUTables) */}
      <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Filters</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {columns.map((col) => (
            <div key={col.field} className="filter" style={{ marginBottom: 8 }}>
              <label style={{ display: "block", marginBottom: 4 }}>
                {col.headerName}
              </label>
              <input
                type="text"
                placeholder={`Filter by ${col.headerName}`}
                value={filters[col.field] || ""}
                onChange={(e) => handleFilterChange(col.field, e.target.value)}
                className="filter-input"
                style={{ width: "100%", padding: 6 }}
              />
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFilterModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <div style={{ marginTop: 12 }} className="total-balance">
        Current Balance: ${currentBalanceUSD()} USD
      </div>
    </div>
  );
}

export default Statement;
